/**
 * Aggregates the 250 ms samples from `startLevelMeter` into the 10 s windows
 * that the level-history breadcrumbs and the silent-recording verdict are
 * defined over.
 */
import {
  combineLevelSamples,
  type LevelSample,
  samplesForMs,
} from "./audio-level";

export const WINDOW_DURATION_MS = 10_000;

/** Samples an unthrottled timer produces in one window. */
export const SAMPLES_PER_WINDOW = samplesForMs(WINDOW_DURATION_MS);

/** Samples a window needs before its silence flags are worth reading. */
export const SAMPLES_FOR_TRUSTED_WINDOW = Math.ceil(SAMPLES_PER_WINDOW * 0.75);

/** One moment's reading, with the context state at that moment. */
export interface LevelReading {
  level: LevelSample;
  contextState: AudioContextState;
}

export interface LevelWindow extends LevelReading {
  /** Window number for this aggregator, counting from zero. */
  index: number;
  /**
   * Wall clock of the sample that opened the window. A consumer measuring one
   * span of a longer session — a recording inside a live stream — uses it to
   * leave out the window that straddles the moment the span began.
   */
  startedAtMs: number;
  samples: number;
  /**
   * False when the window collected far fewer samples than the interval should
   * produce. The timer was throttled, so the window covers much more than
   * `WINDOW_DURATION_MS` and its silence flags say nothing about the recording.
   */
  fullySampled: boolean;
}

export interface LevelWindowAggregator {
  /**
   * @param timestampMs Wall clock (`Date.now()`), not a tick count. Under timer
   * throttling the interval stretches, so counting ticks would report a
   * "fully sampled" window that actually spans far longer than 10 s.
   * @returns The window this sample closed, or null while it is still filling.
   */
  push(reading: LevelReading, timestampMs: number): LevelWindow | null;
}

function summariseWindow(
  index: number,
  startedAtMs: number,
  readings: LevelReading[]
): LevelWindow {
  return {
    index,
    startedAtMs,
    samples: readings.length,
    fullySampled: readings.length >= SAMPLES_FOR_TRUSTED_WINDOW,
    level: combineLevelSamples(readings.map((reading) => reading.level)),
    // One non-running sample is the whole diagnosis, so it survives into the
    // window rather than being outvoted by the running ones.
    contextState:
      readings.find((reading) => reading.contextState !== "running")
        ?.contextState ?? "running",
  };
}

export function createLevelWindowAggregator(): LevelWindowAggregator {
  let index = 0;
  let startedAt: number | null = null;
  let readings: LevelReading[] = [];

  return {
    push(reading, timestampMs) {
      const open =
        startedAt != null && timestampMs - startedAt < WINDOW_DURATION_MS;

      if (open) {
        readings.push(reading);
        return null;
      }

      const closed =
        startedAt == null ? null : summariseWindow(index, startedAt, readings);
      if (closed != null) index += 1;
      // The next window starts at this sample rather than one duration on from
      // the last. A timer frozen for a minute would otherwise close a backlog
      // of windows, one per sample, and exceed the breadcrumb cap.
      startedAt = timestampMs;
      readings = [reading];
      return closed;
    },
  };
}
